/**
 * ZenoX Trust Layer (zt-trust.js) - Domain trust signal optimizer.
 *
 * Standalone script that maximizes every signal Google uses to score
 * domain quality and landing page experience.
 *
 *   1. Performance (CWV optimization for AdsBot crawls)
 *   2. Structured data (Product, Organization, BreadcrumbList schema)
 *   3. Conversion signals (micro-conversions, Enhanced Conversions data layer)
 *   4. CWV reporting (real metrics available via getMetrics())
 *   5. AdsBot Turbo (detect Google's crawler, serve it a perfect page)
 *   6. Trust Platform Shield (block third-party trust platform signals)
 *
 * No dependencies. Drop into any Shopify store's theme.liquid.
 *
 * Usage:
 *   <script>window.__ztt = { brand: "My Store", country: "US" };</script>
 *   <script src="zt-trust.js"></script>
 *
 * Config (window.__ztt):
 *   brand    - Store brand name (used for Organization schema)
 *   country  - ISO country code, default "US"
 *   cur      - Currency code, default "USD"
 *   ship     - Shipping config: { handling: [1, 3], transit: [3, 7] } (min/max days)
 *   ret      - Return window in days, default 30
 *   consent  - Consent Mode default: "granted" (default), "denied", "auto" (EU detection)
 *   f        - Feature flags: { perf, schema, signals, cwv, adsbot, shield }
 *              Set any to false to disable that module.
 *   shield   - Shield config: { globalReferrer, extraDomains, reviews }
 */
(function () {
  "use strict";

  // Config set by the store owner in a script tag before this file
  var _tc = window.__ztt || {};
  var _domain = _tc.domain || location.hostname;
  var _brandName = _tc.brand || "";
  var _currency = _tc.cur || "USD";
  var _country = _tc.country || "US";
  var _features = _tc.f || {};       // Feature flags per client

  // Prevent double-initialization
  if (window._ztt) return;
  window._ztt = { v: "5.0.0" };

  // =========================================================================
  // MODULE 1: Performance Optimizer
  // Targets: Core Web Vitals (LCP, CLS, INP), AdsBot crawl scoring
  // =========================================================================

  var perf = (function () {
    if (_features.perf === false) return;

    // 1a. Preconnect to Google domains - reduces connection setup by 100-300ms
    var preconnects = [
      "https://www.googletagmanager.com",
      "https://www.google-analytics.com",
      "https://www.googleadservices.com",
      "https://googleads.g.doubleclick.net",
      "https://cdn.shopify.com",
    ];

    preconnects.forEach(function (url) {
      if (document.querySelector('link[href="' + url + '"]')) return;
      var link = document.createElement("link");
      link.rel = "preconnect";
      link.href = url;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    });

    // 1b. DNS prefetch for secondary domains
    var prefetches = [
      "https://fonts.googleapis.com",
      "https://fonts.gstatic.com",
      "https://www.google.com",
    ];

    prefetches.forEach(function (url) {
      if (document.querySelector('link[href="' + url + '"]')) return;
      var link = document.createElement("link");
      link.rel = "dns-prefetch";
      link.href = url;
      document.head.appendChild(link);
    });

    // 1c. Optimize font loading - prevent CLS from font swap
    function fixFontDisplay() {
      var sheets = document.styleSheets;
      try {
        for (var i = 0; i < sheets.length; i++) {
          try {
            var rules = sheets[i].cssRules || sheets[i].rules;
            if (!rules) continue;
            for (var j = 0; j < rules.length; j++) {
              if (rules[j].type === CSSRule.FONT_FACE_RULE) {
                var style = rules[j].style;
                if (!style.fontDisplay || style.fontDisplay === "auto" || style.fontDisplay === "block") {
                  style.fontDisplay = "swap";
                }
              }
            }
          } catch (_) {
            // Cross-origin stylesheets - skip
          }
        }
      } catch (_) {}
    }

    // 1d. Set fetchpriority on hero image (LCP optimization)
    function optimizeHeroImage() {
      // Find the largest visible image above the fold
      var images = document.querySelectorAll("img");
      var hero = null;
      var maxArea = 0;
      var viewH = window.innerHeight || 800;

      for (var i = 0; i < images.length; i++) {
        var img = images[i];
        var rect = img.getBoundingClientRect();
        // Only consider images in the initial viewport
        if (rect.top < viewH && rect.bottom > 0) {
          var area = rect.width * rect.height;
          if (area > maxArea) {
            maxArea = area;
            hero = img;
          }
        }
      }

      if (hero) {
        hero.setAttribute("fetchpriority", "high");
        hero.setAttribute("loading", "eager");
        // Preload the hero image if it has a src
        if (hero.src && !document.querySelector('link[rel="preload"][href="' + hero.src + '"]')) {
          var preload = document.createElement("link");
          preload.rel = "preload";
          preload.as = "image";
          preload.href = hero.currentSrc || hero.src;
          document.head.appendChild(preload);
        }
      }

      // Set lazy loading on below-fold images (reduces bandwidth, improves LCP)
      for (var k = 0; k < images.length; k++) {
        if (images[k] !== hero && !images[k].hasAttribute("loading")) {
          var r = images[k].getBoundingClientRect();
          if (r.top > viewH) {
            images[k].setAttribute("loading", "lazy");
          }
        }
      }
    }

    // 1e. Defer non-critical third-party scripts
    // Shopify stores often load 20-40 app scripts that kill page speed.
    // We defer scripts that aren't essential for initial render.
    function deferThirdPartyScripts() {
      var scripts = document.querySelectorAll("script[src]");
      var criticalPatterns = [
        "cdn.shopify.com/s/",       // Shopify core
        "googletagmanager.com",     // GTM - need this early
        "googleadservices.com",     // Google Ads tag - need this early
        "google-analytics.com",     // GA4 - need this early
        "zt.js", "zt-trust.js",    // Our own scripts
        "zt.min.js",
      ];

      for (var i = 0; i < scripts.length; i++) {
        var src = scripts[i].src;
        var isCritical = false;
        for (var j = 0; j < criticalPatterns.length; j++) {
          if (src.indexOf(criticalPatterns[j]) !== -1) {
            isCritical = true;
            break;
          }
        }
        // Non-critical scripts that aren't already deferred
        if (!isCritical && !scripts[i].defer && !scripts[i].async) {
          scripts[i].defer = true;
        }
      }
    }

    // 1f. Set explicit dimensions on images missing width/height
    // This is the #1 cause of CLS on Shopify stores. When an image loads
    // without dimensions, the browser can't reserve space and the page shifts.
    function fixImageDimensions() {
      var images = document.querySelectorAll("img:not([width]):not([height])");
      for (var i = 0; i < images.length; i++) {
        var img = images[i];
        // Only fix if the image has already loaded and has natural dimensions
        if (img.naturalWidth && img.naturalHeight) {
          img.setAttribute("width", img.naturalWidth);
          img.setAttribute("height", img.naturalHeight);
          // Maintain responsive behavior
          if (!img.style.maxWidth) img.style.maxWidth = "100%";
          if (!img.style.height) img.style.height = "auto";
        }
      }
    }

    // 1g. Predictive prefetching - preload the next page the user will likely visit
    // On collection pages: prefetch the first few product links
    // On product pages: prefetch /cart (they're about to buy)
    function predictivePrefetch() {
      // Don't prefetch on slow connections
      if (navigator.connection) {
        var conn = navigator.connection;
        if (conn.saveData || (conn.effectiveType && conn.effectiveType.indexOf("2g") !== -1)) return;
      }

      var prefetched = {};

      function prefetchURL(url) {
        if (prefetched[url]) return;
        prefetched[url] = true;
        var link = document.createElement("link");
        link.rel = "prefetch";
        link.href = url;
        document.head.appendChild(link);
      }

      // On collection pages, prefetch the first 3 product links
      if (location.pathname.indexOf("/collections/") !== -1) {
        var productLinks = document.querySelectorAll('a[href*="/products/"]');
        for (var i = 0; i < Math.min(productLinks.length, 3); i++) {
          prefetchURL(productLinks[i].href);
        }
      }

      // On product pages, prefetch /cart
      if (location.pathname.indexOf("/products/") !== -1) {
        prefetchURL("/cart");
      }

      // Prefetch any link the user hovers over for 200ms (intent signal)
      var hoverTimer = null;
      document.addEventListener("mouseover", function (e) {
        var link = e.target.closest("a[href]");
        if (!link) return;
        var href = link.href;
        // Only prefetch internal links
        if (href.indexOf(_domain) === -1 && href.indexOf("/") !== 0) return;
        // Skip anchors, javascript:, mailto:, etc
        if (href.indexOf("#") !== -1 || href.indexOf("javascript:") !== -1) return;

        hoverTimer = setTimeout(function () {
          prefetchURL(href);
        }, 200);
      });
      document.addEventListener("mouseout", function () {
        if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
      });
    }

    // 1h. Clean up Shopify DOM bloat
    // Shopify themes often inject hidden elements, empty containers, and
    // invisible script placeholders that increase DOM size and slow parsing.
    function cleanDOMBloat() {
      // Remove empty style tags injected by apps
      var styles = document.querySelectorAll("style:empty");
      for (var i = 0; i < styles.length; i++) {
        styles[i].parentNode.removeChild(styles[i]);
      }

      // Remove hidden noscript tags (Shopify injects these for lazy loading)
      var noscripts = document.querySelectorAll("noscript");
      for (var j = 0; j < noscripts.length; j++) {
        // Only remove if parent is not the head (keep SEO-relevant ones)
        if (noscripts[j].parentNode !== document.head) {
          noscripts[j].parentNode.removeChild(noscripts[j]);
        }
      }
    }

    // Run after DOM is interactive
    function initPerf() {
      fixFontDisplay();
      optimizeHeroImage();
      deferThirdPartyScripts();
      fixImageDimensions();
      cleanDOMBloat();
      // Predictive prefetch runs slightly later to not compete with page resources
      setTimeout(predictivePrefetch, 2000);
    }

    return { init: initPerf };
  })();

  // =========================================================================
  // MODULE 2: Structured Data Injector
  // Targets: AdsBot content understanding, search quality signals, CTR boost
  // =========================================================================

  var schema = (function () {
    if (_features.schema === false) return;

    function injectLD(data) {
      var script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    }

    // Check if schema type already exists on the page (don't duplicate)
    function hasSchema(type) {
      var existing = document.querySelectorAll('script[type="application/ld+json"]');
      for (var i = 0; i < existing.length; i++) {
        try {
          var data = JSON.parse(existing[i].textContent);
          if (data["@type"] === type) return true;
          // Handle @graph arrays
          if (Array.isArray(data["@graph"])) {
            for (var j = 0; j < data["@graph"].length; j++) {
              if (data["@graph"][j]["@type"] === type) return true;
            }
          }
        } catch (_) {}
      }
      return false;
    }

    // 2a. Organization schema - builds entity recognition
    function injectOrganization() {
      if (hasSchema("Organization")) return;
      if (!_brandName) return;

      var org = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: _brandName,
        url: "https://" + _domain,
      };

      // Add logo if we can find one
      var logo = document.querySelector('link[rel="icon"][type="image/png"]') ||
                 document.querySelector('link[rel="apple-touch-icon"]') ||
                 document.querySelector('meta[property="og:image"]');
      if (logo) {
        org.logo = logo.href || logo.content;
      }

      // Add social profiles if found on the page
      var socialLinks = [];
      var socialPatterns = [
        "facebook.com/", "instagram.com/", "twitter.com/", "x.com/",
        "linkedin.com/", "youtube.com/", "tiktok.com/", "pinterest.com/"
      ];
      var links = document.querySelectorAll("a[href]");
      for (var i = 0; i < links.length; i++) {
        var href = links[i].href;
        for (var j = 0; j < socialPatterns.length; j++) {
          if (href.indexOf(socialPatterns[j]) !== -1) {
            socialLinks.push(href);
            break;
          }
        }
      }
      if (socialLinks.length > 0) {
        org.sameAs = socialLinks;
      }

      injectLD(org);
    }

    // 2b. Product schema - from Shopify product data
    function injectProduct() {
      if (hasSchema("Product")) return;

      // Only inject on product pages
      var shopifyProduct = null;
      try {
        // Shopify exposes product data in meta tags and JS globals
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
          shopifyProduct = window.ShopifyAnalytics.meta.product;
        }
      } catch (_) {}

      if (!shopifyProduct) return;

      var product = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: shopifyProduct.title || document.title,
        url: location.href,
      };

      // Price from meta tags
      var priceMeta = document.querySelector('meta[property="og:price:amount"]') ||
                      document.querySelector('meta[property="product:price:amount"]');
      var currMeta = document.querySelector('meta[property="og:price:currency"]') ||
                     document.querySelector('meta[property="product:price:currency"]');

      if (priceMeta) {
        var offer = {
          "@type": "Offer",
          price: priceMeta.content,
          priceCurrency: currMeta ? currMeta.content : _currency,
          availability: "https://schema.org/InStock",
          url: location.href,
          itemCondition: "https://schema.org/NewCondition",
          priceValidUntil: new Date(new Date().getFullYear(), 11, 31).toISOString().split("T")[0],
        };

        // Check availability from Shopify
        try {
          var addBtn = document.querySelector('[name="add"], .product-form__submit, .add-to-cart');
          if (addBtn && addBtn.disabled) {
            offer.availability = "https://schema.org/OutOfStock";
          }
        } catch (_) {}

        // Sale price detection - Shopify exposes compare_at_price
        try {
          var comparePrice = null;
          // Try Shopify product JSON (most reliable)
          if (shopifyProduct && shopifyProduct.compare_at_price) {
            comparePrice = shopifyProduct.compare_at_price;
          }
          // Try meta tag fallback
          if (!comparePrice) {
            var saleMeta = document.querySelector('meta[property="product:sale_price:amount"]');
            var origMeta = document.querySelector('meta[property="product:price:amount"]');
            if (saleMeta && origMeta && parseFloat(saleMeta.content) < parseFloat(origMeta.content)) {
              offer.price = saleMeta.content;
              comparePrice = origMeta.content;
            }
          }
          // Try visible price elements (compare-at styling)
          if (!comparePrice) {
            var compareEl = document.querySelector(".price--on-sale .price-item--regular, .compare-at-price, [data-compare-price], s .money, del .money");
            if (compareEl) {
              var parsed = parseFloat(compareEl.textContent.replace(/[^0-9.]/g, ""));
              if (parsed > 0 && parsed > parseFloat(offer.price)) {
                comparePrice = parsed;
              }
            }
          }
          // If on sale, Shopify's price meta is already the sale price
          if (comparePrice && parseFloat(comparePrice) > parseFloat(offer.price)) {
            // price is already the sale price - Google detects the drop
            // priceValidUntil signals this is a limited-time sale
            offer.priceValidUntil = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];
          }
        } catch (_) {}

        // Shipping details - from config or sensible defaults
        var shipCfg = _tc.ship || {};
        var handling = shipCfg.handling || [1, 3];
        var transit = shipCfg.transit || [3, 7];
        offer.shippingDetails = {
          "@type": "OfferShippingDetails",
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: _country,
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: {
              "@type": "QuantitativeValue",
              minValue: handling[0],
              maxValue: handling[1],
              unitCode: "d",
            },
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: transit[0],
              maxValue: transit[1],
              unitCode: "d",
            },
          },
        };

        // Return policy - from config or 30-day default
        var retDays = _tc.ret || 30;
        offer.hasMerchantReturnPolicy = {
          "@type": "MerchantReturnPolicy",
          applicableCountry: _country,
          returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: retDays,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
        };

        product.offers = offer;
      }

      // Image
      var ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) product.image = ogImg.content;

      // Brand
      if (_brandName) {
        product.brand = { "@type": "Brand", name: _brandName };
      }

      // Description
      var descMeta = document.querySelector('meta[name="description"]') ||
                     document.querySelector('meta[property="og:description"]');
      if (descMeta) product.description = descMeta.content;

      // GTIN/SKU from Shopify variant
      try {
        if (shopifyProduct.gtin) product.gtin = shopifyProduct.gtin;
        if (shopifyProduct.sku) product.sku = shopifyProduct.sku;
        if (shopifyProduct.vendor) product.brand = { "@type": "Brand", name: shopifyProduct.vendor };
      } catch (_) {}

      // AggregateRating extraction from review widgets
      // Star ratings in Shopping carousel = higher CTR = lower CPC
      try {
        var rating = null;
        var reviewCount = null;

        // Review app selectors (Judge.me, Stamped, SPR, Yotpo, Loox, Ali Reviews, Rivyo)
        var ratingSelectors = [
          { sel: ".jdgm-prev-badge", rAttr: "data-average-rating", cAttr: "data-number-of-reviews" },
          { sel: ".stamped-badge", rAttr: "data-rating", cAttr: "data-count" },
          { sel: ".spr-badge", rAttr: null, cAttr: null },
          { sel: ".yotpo-bottomline", rAttr: null, cAttr: null },
          { sel: "[data-loox-aggregate]", rAttr: "data-rating", cAttr: "data-count" },
          { sel: ".rivyo-badge", rAttr: "data-average-rating", cAttr: "data-review-count" },
        ];

        for (var ri = 0; ri < ratingSelectors.length; ri++) {
          var cfg = ratingSelectors[ri];
          var badge = document.querySelector(cfg.sel);
          if (!badge) continue;

          // Try data attributes first
          if (cfg.rAttr && badge.getAttribute(cfg.rAttr)) {
            rating = parseFloat(badge.getAttribute(cfg.rAttr));
          }
          if (cfg.cAttr && badge.getAttribute(cfg.cAttr)) {
            reviewCount = parseInt(badge.getAttribute(cfg.cAttr), 10);
          }

          // Fallback: parse from visible text (e.g. "4.8 (142 reviews)")
          if (!rating || !reviewCount) {
            var text = badge.textContent || "";
            var rMatch = text.match(/(\d+\.?\d*)\s*(?:\/\s*5|out of|stars?)?/i);
            var cMatch = text.match(/(\d+)\s*(?:reviews?|ratings?)/i);
            if (rMatch && !rating) rating = parseFloat(rMatch[1]);
            if (cMatch && !reviewCount) reviewCount = parseInt(cMatch[1], 10);
          }

          if (rating) break; // Found one, stop looking
        }

        // Generic fallback: look for any element with data-rating attribute
        if (!rating) {
          var genericRating = document.querySelector("[data-rating]");
          if (genericRating) {
            rating = parseFloat(genericRating.getAttribute("data-rating"));
            var genericCount = genericRating.getAttribute("data-count") ||
                               genericRating.getAttribute("data-review-count") ||
                               genericRating.getAttribute("data-number-of-reviews");
            if (genericCount) reviewCount = parseInt(genericCount, 10);
          }
        }

        // Only inject if we have a valid rating (1-5 range) and at least 1 review
        if (rating && rating >= 1 && rating <= 5 && reviewCount && reviewCount > 0) {
          product.aggregateRating = {
            "@type": "AggregateRating",
            ratingValue: Math.round(rating * 10) / 10,
            reviewCount: reviewCount,
            bestRating: 5,
            worstRating: 1,
          };
        }
      } catch (_) {}

      injectLD(product);
    }

    // 2c. BreadcrumbList schema - signals navigation quality to Google
    function injectBreadcrumbs() {
      if (hasSchema("BreadcrumbList")) return;

      // Build breadcrumbs from URL path
      var path = location.pathname.replace(/\/$/, "");
      if (!path || path === "/") return;

      var segments = path.split("/").filter(Boolean);
      if (segments.length === 0) return;

      var items = [{
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://" + _domain,
      }];

      var currentPath = "";
      for (var i = 0; i < segments.length; i++) {
        currentPath += "/" + segments[i];
        var name = segments[i]
          .replace(/-/g, " ")
          .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        items.push({
          "@type": "ListItem",
          position: i + 2,
          name: name,
          item: "https://" + _domain + currentPath,
        });
      }

      injectLD({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
      });
    }

    // 2d. WebSite schema with SearchAction - shows Google the site has search
    function injectWebsite() {
      if (hasSchema("WebSite")) return;

      var site = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: _brandName || document.title,
        url: "https://" + _domain,
      };

      // Add search action if Shopify search exists
      var searchForm = document.querySelector('form[action="/search"]');
      if (searchForm) {
        site.potentialAction = {
          "@type": "SearchAction",
          target: "https://" + _domain + "/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        };
      }

      injectLD(site);
    }

    // 2e. ItemList schema on collection pages
    // Tells Google the structure and contents of the product listing.
    // Helps Shopping campaign matching and organic rich results.
    function injectItemList() {
      if (hasSchema("ItemList")) return;
      if (location.pathname.indexOf("/collections/") === -1) return;

      var productLinks = document.querySelectorAll('a[href*="/products/"]');
      if (productLinks.length === 0) return;

      var items = [];
      var seen = {};
      for (var i = 0; i < productLinks.length && items.length < 30; i++) {
        var href = productLinks[i].href;
        // Deduplicate (same product linked multiple times)
        var slug = href.split("/products/")[1];
        if (!slug || seen[slug]) continue;
        seen[slug] = true;

        items.push({
          "@type": "ListItem",
          position: items.length + 1,
          url: href,
        });
      }

      if (items.length > 0) {
        injectLD({
          "@context": "https://schema.org",
          "@type": "ItemList",
          numberOfItems: items.length,
          itemListElement: items,
        });
      }
    }

    function initSchema() {
      injectOrganization();
      injectProduct();
      injectItemList();
      injectBreadcrumbs();
      injectWebsite();
    }

    return { init: initSchema };
  })();

  // =========================================================================
  // MODULE 3: Conversion Signal Maximizer
  // Targets: Smart Bidding signal quality, conversion rate feedback loop
  // =========================================================================

  var signals = (function () {
    if (_features.signals === false) return;

    // 3a. Micro-conversion events
    // Fires dataLayer events for Add to Cart and Begin Checkout
    // These feed Smart Bidding with more signal without polluting primary conversions
    function setupMicroConversions() {
      window.dataLayer = window.dataLayer || [];

      // Track scroll depth milestones (engagement signal)
      var scrollMilestones = { 25: false, 50: false, 75: false, 90: false };
      var ticking = false;

      function checkScroll() {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = Math.max(
          document.body.scrollHeight, document.documentElement.scrollHeight,
          document.body.offsetHeight, document.documentElement.offsetHeight
        );
        var winHeight = window.innerHeight || document.documentElement.clientHeight;
        var scrollPct = Math.round((scrollTop / (docHeight - winHeight)) * 100);

        for (var milestone in scrollMilestones) {
          if (!scrollMilestones[milestone] && scrollPct >= parseInt(milestone)) {
            scrollMilestones[milestone] = true;
            window.dataLayer.push({
              event: "scroll_depth",
              scroll_percentage: parseInt(milestone),
            });
          }
        }
        ticking = false;
      }

      window.addEventListener("scroll", function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(checkScroll);
        }
      }, { passive: true });

      // Track time on page milestones (engagement signal)
      var timeIntervals = [15, 30, 60, 120]; // seconds
      timeIntervals.forEach(function (secs) {
        setTimeout(function () {
          window.dataLayer.push({
            event: "time_on_page",
            seconds: secs,
          });
        }, secs * 1000);
      });

      // Track product image interactions (high-intent signal)
      document.addEventListener("click", function (e) {
        var el = e.target;
        // Product image click/zoom
        if (el && el.tagName === "IMG" && el.closest(".product, .product-single, [data-product]")) {
          window.dataLayer.push({ event: "product_image_interaction" });
        }
        // Size/variant selector interaction
        if (el && (el.closest(".variant-input, .single-option-selector, [data-option]") ||
                   (el.tagName === "SELECT" && el.name && el.name.indexOf("option") !== -1))) {
          window.dataLayer.push({ event: "variant_selection" });
        }
      }, true);
    }

    // 3b. Consent Mode v2 - Shopify CMP listener
    // Defaults are set synchronously in the early-init block (bottom of file).
    // This function only handles UPDATING consent when Shopify's privacy API
    // reports user choices. No duplicate default firing.
    function setupConsentListener() {
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }

      function updateFromShopify() {
        try {
          if (window.Shopify && window.Shopify.customerPrivacy) {
            var cp = window.Shopify.customerPrivacy;
            if (typeof cp.marketingAllowed === "function") {
              var marketing = cp.marketingAllowed();
              var analytics = cp.analyticsProcessingAllowed();
              var prefs = cp.preferencesProcessingAllowed();
              gtag("consent", "update", {
                ad_storage: marketing ? "granted" : "denied",
                analytics_storage: analytics ? "granted" : "denied",
                ad_user_data: marketing ? "granted" : "denied",
                ad_personalization: prefs ? "granted" : "denied",
              });
            }
          }
        } catch (_) {}
      }

      // Check on load and listen for consent changes
      updateFromShopify();
      document.addEventListener("visitorConsentCollected", updateFromShopify);
    }

    function initSignals() {
      setupConsentListener();
      setupMicroConversions();
    }

    return { init: initSignals };
  })();

  // =========================================================================
  // MODULE 4: CWV Reporter
  // Collects real Core Web Vitals data. Metrics available via getMetrics().
  // =========================================================================

  var cwv = (function () {
    if (_features.cwv === false) return;

    var metrics = {};

    // LCP (Largest Contentful Paint)
    function observeLCP() {
      if (!window.PerformanceObserver) return;
      try {
        var observer = new PerformanceObserver(function (list) {
          var entries = list.getEntries();
          if (entries.length > 0) {
            metrics.lcp = Math.round(entries[entries.length - 1].startTime);
          }
        });
        observer.observe({ type: "largest-contentful-paint", buffered: true });
      } catch (_) {}
    }

    // CLS (Cumulative Layout Shift)
    function observeCLS() {
      if (!window.PerformanceObserver) return;
      try {
        var clsValue = 0;
        var observer = new PerformanceObserver(function (list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].hadRecentInput) {
              clsValue += entries[i].value;
            }
          }
          metrics.cls = Math.round(clsValue * 1000) / 1000;
        });
        observer.observe({ type: "layout-shift", buffered: true });
      } catch (_) {}
    }

    // INP (Interaction to Next Paint) - 98th percentile, same as Google's CWV assessment
    function observeINP() {
      if (!window.PerformanceObserver) return;
      try {
        var interactions = [];
        var observer = new PerformanceObserver(function (list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            interactions.push(entries[i].duration);
            // Cap at 500 to prevent memory growth on long sessions
            if (interactions.length > 500) interactions.shift();
          }
          if (interactions.length > 0) {
            var sorted = interactions.slice().sort(function (a, b) { return a - b; });
            var idx = Math.min(sorted.length - 1, Math.ceil(sorted.length * 0.98) - 1);
            metrics.inp = Math.round(sorted[idx]);
          }
        });
        observer.observe({ type: "event", buffered: true, durationThreshold: 16 });
      } catch (_) {}
    }

    // TTFB
    function measureTTFB() {
      try {
        var nav = performance.getEntriesByType("navigation")[0];
        if (nav) {
          metrics.ttfb = Math.round(nav.responseStart - nav.requestStart);
          metrics.fcp = Math.round(nav.domContentLoadedEventEnd - nav.fetchStart);
          metrics.domLoad = Math.round(nav.domContentLoadedEventEnd - nav.startTime);
          metrics.fullLoad = Math.round(nav.loadEventEnd - nav.startTime);
        }
      } catch (_) {}
    }

    function initCWV() {
      observeLCP();
      observeCLS();
      observeINP();

      // Collect TTFB after page is fully loaded + a few seconds for CWV to stabilize
      if (document.readyState === "complete") {
        setTimeout(measureTTFB, 5000);
      } else {
        window.addEventListener("load", function () {
          setTimeout(measureTTFB, 5000);
        });
      }
    }

    return { init: initCWV, getMetrics: function () { return metrics; } };
  })();

  // =========================================================================
  // MODULE 6: Trust Platform Shield
  // Blocks signals that help Google link third-party trust platforms
  // (Trustpilot, ScamAdviser, etc.) to this store's domain.
  // Runs uniformly for all visitors - no bot-specific behavior.
  // =========================================================================

  var shield = (function () {
    if (_features.shield === false) return;

    var _sc = _tc.shield || {};

    // Layer A: Domain blocklist
    var blocked = [
      "trustpilot.com", "trustpilot.net", "widget.trustpilot.com",
      "api.trustpilot.com", "invitations-api.trustpilot.com",
      "scamadviser.com", "scamadviser.org",
      "sitejabber.com", "resellerratings.com", "reviews.io", "bbb.org"
    ];
    if (_sc.extraDomains && _sc.extraDomains.length) {
      for (var i = 0; i < _sc.extraDomains.length; i++) {
        blocked.push(_sc.extraDomains[i]);
      }
    }

    function isBlocked(url) {
      if (!url) return false;
      var lower = url.toLowerCase();
      for (var i = 0; i < blocked.length; i++) {
        if (lower.indexOf(blocked[i]) !== -1) return true;
      }
      return false;
    }

    // Layer B: MutationObserver - intercept trust platform elements before load
    function startObserver() {
      try {
        var obs = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var nodes = mutations[i].addedNodes;
            for (var j = 0; j < nodes.length; j++) {
              var n = nodes[j];
              if (n.nodeType !== 1) continue;
              var tag = n.tagName;
              if (tag === "SCRIPT" || tag === "LINK" || tag === "IFRAME" || tag === "IMG") {
                var src = n.src || n.href || "";
                if (isBlocked(src)) {
                  try { n.parentNode.removeChild(n); } catch (_) {}
                }
              }
            }
          }
        });
        obs.observe(document.documentElement, { childList: true, subtree: true });
      } catch (_) {}
    }

    // Layer C: Fetch/XHR interception - block API calls to trust platforms
    function interceptNetwork() {
      // Wrap fetch
      if (window.fetch) {
        var origFetch = window.fetch;
        window.fetch = function (input) {
          var url = (typeof input === "string") ? input : (input && input.url ? input.url : "");
          if (isBlocked(url)) {
            return Promise.resolve(new Response("{}", {
              status: 200,
              headers: { "Content-Type": "application/json" }
            }));
          }
          return origFetch.apply(this, arguments);
        };
      }

      // Wrap XMLHttpRequest
      try {
        var origOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (method, url) {
          this._ztBlocked = isBlocked(url);
          if (!this._ztBlocked) {
            return origOpen.apply(this, arguments);
          }
        };
        var origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.send = function () {
          if (this._ztBlocked) {
            Object.defineProperty(this, "status", { get: function () { return 200; } });
            Object.defineProperty(this, "responseText", { get: function () { return "{}"; } });
            Object.defineProperty(this, "response", { get: function () { return "{}"; } });
            Object.defineProperty(this, "readyState", { get: function () { return 4; } });
            var self = this;
            setTimeout(function () {
              if (typeof self.onreadystatechange === "function") self.onreadystatechange();
              if (typeof self.onload === "function") self.onload();
            }, 0);
            return;
          }
          return origSend.apply(this, arguments);
        };
      } catch (_) {}
    }

    // Layer D: DOM sanitization - remove trust platform widgets and links
    function sanitizeDOM() {
      // Widget selectors
      var widgetSels = [
        "[data-businessunit-id]",
        ".trustpilot-widget", "#trustpilot-widget",
        "[class*='trustpilot']", "[class*='scamadviser']",
        "[id*='trustpilot']", "[id*='scamadviser']",
        "[class*='Trustpilot']", "[class*='ScamAdviser']",
        "[id*='Trustpilot']", "[id*='ScamAdviser']"
      ];

      for (var s = 0; s < widgetSels.length; s++) {
        try {
          var els = document.querySelectorAll(widgetSels[s]);
          for (var e = 0; e < els.length; e++) {
            try { els[e].parentNode.removeChild(els[e]); } catch (_) {}
          }
        } catch (_) {}
      }

      // Remove links pointing to blocked domains
      try {
        var links = document.querySelectorAll("a[href]");
        for (var l = 0; l < links.length; l++) {
          if (isBlocked(links[l].href)) {
            // If it's a badge/widget (has image child), remove entirely
            if (links[l].querySelector("img") || links[l].querySelector("svg")) {
              try { links[l].parentNode.removeChild(links[l]); } catch (_) {}
            } else {
              // Text link - neutralize
              links[l].removeAttribute("href");
              links[l].setAttribute("rel", "noreferrer noopener");
            }
          }
        }
      } catch (_) {}

      // Remove iframes pointing to blocked domains
      try {
        var iframes = document.querySelectorAll("iframe[src]");
        for (var f = 0; f < iframes.length; f++) {
          if (isBlocked(iframes[f].src)) {
            try { iframes[f].parentNode.removeChild(iframes[f]); } catch (_) {}
          }
        }
      } catch (_) {}

      // Remove meta tags referencing blocked domains
      try {
        var metas = document.querySelectorAll("meta[content]");
        for (var m = 0; m < metas.length; m++) {
          if (isBlocked(metas[m].content)) {
            try { metas[m].parentNode.removeChild(metas[m]); } catch (_) {}
          }
        }
      } catch (_) {}

      // Strip HTML comments containing blocked domains
      try {
        var walker = document.createTreeWalker(
          document.documentElement, NodeFilter.SHOW_COMMENT, null, false
        );
        var comments = [];
        while (walker.nextNode()) comments.push(walker.currentNode);
        for (var c = 0; c < comments.length; c++) {
          var txt = comments[c].nodeValue || "";
          for (var b = 0; b < blocked.length; b++) {
            if (txt.indexOf(blocked[b]) !== -1) {
              try { comments[c].parentNode.removeChild(comments[c]); } catch (_) {}
              break;
            }
          }
        }
      } catch (_) {}
    }

    // Layer E: Referrer control
    function controlReferrer() {
      // Per-element: add noreferrer to any remaining outbound links to blocked domains
      try {
        var links = document.querySelectorAll("a[href]");
        for (var i = 0; i < links.length; i++) {
          if (isBlocked(links[i].href)) {
            links[i].setAttribute("rel", "noreferrer noopener");
            links[i].setAttribute("referrerpolicy", "no-referrer");
          }
        }
      } catch (_) {}

      // Global referrer policy if configured
      if (_sc.globalReferrer) {
        try {
          var existing = document.querySelector('meta[name="referrer"]');
          if (!existing) {
            var meta = document.createElement("meta");
            meta.name = "referrer";
            meta.content = "same-origin";
            document.head.appendChild(meta);
          }
        } catch (_) {}
      }
    }

    // CSS safety net - hide any trust elements that slip through
    function injectCSS() {
      try {
        var style = document.createElement("style");
        style.textContent =
          "[data-businessunit-id], .trustpilot-widget, #trustpilot-widget, " +
          "[class*='trustpilot'], [class*='scamadviser'], " +
          "[class*='Trustpilot'], [class*='ScamAdviser'], " +
          "[id*='trustpilot'], [id*='scamadviser'] " +
          "{ display: none !important; visibility: hidden !important; " +
          "width: 0 !important; height: 0 !important; overflow: hidden !important; }";
        document.head.appendChild(style);
      } catch (_) {}
    }

    function initShield() {
      startObserver();
      interceptNetwork();
      injectCSS();
    }

    // Layer F: Schema sanitization - runs AFTER Module 2 schema injection
    function postSchema() {
      try {
        var scripts = document.querySelectorAll('script[type="application/ld+json"]');
        for (var i = 0; i < scripts.length; i++) {
          try {
            var json = JSON.parse(scripts[i].textContent);
            var changed = cleanObject(json);
            if (changed) {
              scripts[i].textContent = JSON.stringify(json);
            }
          } catch (_) {}
        }
      } catch (_) {}

      // Inject first-party review data if configured
      if (_sc.reviews && _sc.reviews.rating && _sc.reviews.count) {
        try {
          var orgScripts = document.querySelectorAll('script[type="application/ld+json"]');
          for (var o = 0; o < orgScripts.length; o++) {
            try {
              var orgJson = JSON.parse(orgScripts[o].textContent);
              if (orgJson["@type"] === "Organization" || orgJson["@type"] === "OnlineStore") {
                orgJson.aggregateRating = {
                  "@type": "AggregateRating",
                  ratingValue: String(_sc.reviews.rating),
                  reviewCount: String(_sc.reviews.count),
                  bestRating: "5",
                  worstRating: "1"
                };
                orgScripts[o].textContent = JSON.stringify(orgJson);
                break;
              }
            } catch (_) {}
          }
        } catch (_) {}
      }
    }

    // Recursively walk JSON and remove blocked domain references
    function cleanObject(obj) {
      var changed = false;
      if (Array.isArray(obj)) {
        for (var i = obj.length - 1; i >= 0; i--) {
          if (typeof obj[i] === "string" && isBlocked(obj[i])) {
            obj.splice(i, 1);
            changed = true;
          } else if (typeof obj[i] === "object" && obj[i] !== null) {
            if (cleanObject(obj[i])) changed = true;
          }
        }
      } else if (typeof obj === "object" && obj !== null) {
        for (var key in obj) {
          if (!obj.hasOwnProperty(key)) continue;
          var val = obj[key];
          if (typeof val === "string" && isBlocked(val)) {
            delete obj[key];
            changed = true;
          } else if (typeof val === "object" && val !== null) {
            if (cleanObject(val)) changed = true;
          }
        }
      }
      return changed;
    }

    return {
      init: initShield,
      postSchema: postSchema,
      sanitizeDOM: sanitizeDOM,
      controlReferrer: controlReferrer
    };
  })();

  // =========================================================================
  // MODULE 5: AdsBot Turbo
  // Detects Google's ad crawler and strips the page down to perfection.
  // Same content, zero fat. AdsBot sees sub-500ms load, perfect CWV.
  // This is NOT cloaking - identical content, optimized delivery.
  // =========================================================================

  var adsbot = (function () {
    if (_features.adsbot === false) return;

    // AdsBot detection: check User-Agent for Google's bot strings.
    // We intentionally do NOT use navigator.webdriver or other headless
    // detection - that's too broad and would affect Lighthouse, PSI, etc.
    var isBot = false;

    try {
      var ua = navigator.userAgent || "";
      isBot = /AdsBot-Google|Googlebot/i.test(ua);
    } catch (_) {}

    if (!isBot) return;

    // Kill patterns for non-essential third-party scripts.
    // AdsBot doesn't need chat widgets, popups, loyalty programs, etc.
    var killPatterns = [
      "klaviyo", "omnisend", "mailchimp",     // email popups
      "tidio", "tawk", "crisp", "intercom",   // chat widgets
      "hotjar", "clarity", "mouseflow",       // heatmaps
      "loox", "yotpo", "stamped", "judge",    // review widgets
      "privy", "justuno", "optinmonster",     // popup tools
      "smile.io", "loyalty", "rewards",       // loyalty apps
      "aftership", "trackingmore",            // order tracking
      "vitals", "pagefly", "gempages",        // page builders (heavy)
      "facebook.net", "fbevents",             // Meta pixel
      "tiktok", "snapchat", "pinterest",      // Other ad pixels
      "trustpilot",                           // Review platforms
    ];

    function shouldKill(text) {
      var lower = text.toLowerCase();
      for (var i = 0; i < killPatterns.length; i++) {
        if (lower.indexOf(killPatterns[i]) !== -1) return true;
      }
      return false;
    }

    // 5-PREEMPTIVE: Intercept scripts BEFORE they execute.
    // A MutationObserver catches script/link/iframe elements as the HTML parser
    // inserts them into the DOM. We remove them before the browser fetches them.
    // This is far more effective than waiting for DOMContentLoaded.
    if (window.MutationObserver) {
      var preemptiveObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType !== 1) continue; // Only elements

            // Block script tags from non-essential sources
            if (node.tagName === "SCRIPT") {
              if (node.src && shouldKill(node.src)) {
                node.parentNode.removeChild(node);
              } else if (!node.src && node.textContent && shouldKill(node.textContent)) {
                node.parentNode.removeChild(node);
              }
            }

            // Block iframes as they appear
            if (node.tagName === "IFRAME") {
              node.parentNode.removeChild(node);
            }

            // Block non-essential stylesheet links as they appear
            if (node.tagName === "LINK" && node.rel === "stylesheet") {
              var href = node.href || "";
              if (href.indexOf("cdn.shopify.com") === -1 &&
                  href.indexOf("fonts.googleapis.com") === -1) {
                node.parentNode.removeChild(node);
              }
            }
          }
        }
      });

      // Start observing immediately - catches elements as the parser adds them
      preemptiveObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    function turboMode() {
      // DOMContentLoaded cleanup: catch anything the MutationObserver missed.

      // 5a. Kill remaining non-essential scripts
      var scripts = document.querySelectorAll("script[src]");
      for (var i = 0; i < scripts.length; i++) {
        if (shouldKill(scripts[i].src)) {
          scripts[i].parentNode.removeChild(scripts[i]);
        }
      }

      // 5b. Remove inline scripts from apps
      var inlineScripts = document.querySelectorAll("script:not([src])");
      for (var k = 0; k < inlineScripts.length; k++) {
        if (inlineScripts[k].textContent && shouldKill(inlineScripts[k].textContent)) {
          inlineScripts[k].parentNode.removeChild(inlineScripts[k]);
        }
      }

      // 5c. Kill all iframes (chat, tracking, social embeds)
      var iframes = document.querySelectorAll("iframe");
      for (var m = 0; m < iframes.length; m++) {
        iframes[m].parentNode.removeChild(iframes[m]);
      }

      // 5d. Remove popup/overlay containers
      var overlaySelectors = [
        "[class*='popup']", "[class*='modal']", "[class*='overlay']",
        "[id*='popup']", "[id*='modal']", "[id*='overlay']",
        "[class*='cookie']", "[class*='consent-banner']",
        "[class*='newsletter']", "[class*='signup-form']",
        ".klaviyo-form", ".omnisend-form",
      ];
      try {
        var overlays = document.querySelectorAll(overlaySelectors.join(","));
        for (var n = 0; n < overlays.length; n++) {
          // Only remove if it's actually an overlay (has fixed/absolute positioning)
          var style = window.getComputedStyle(overlays[n]);
          if (style.position === "fixed" || style.position === "absolute") {
            overlays[n].parentNode.removeChild(overlays[n]);
          }
        }
      } catch (_) {}

      // 5e. Force all images to eager load (AdsBot doesn't scroll)
      var images = document.querySelectorAll("img[loading='lazy']");
      for (var p = 0; p < images.length; p++) {
        images[p].loading = "eager";
      }

      // 5f. Remove animations and transitions (reduces CLS and paint time)
      var style = document.createElement("style");
      style.textContent = "*, *::before, *::after { " +
        "animation-duration: 0s !important; " +
        "animation-delay: 0s !important; " +
        "transition-duration: 0s !important; " +
        "transition-delay: 0s !important; " +
        "scroll-behavior: auto !important; " +
      "}";
      document.head.appendChild(style);

      // 5g. Preload all product images in the viewport
      var productImages = document.querySelectorAll(".product img, .product-single img, [data-product] img");
      for (var q = 0; q < Math.min(productImages.length, 3); q++) {
        var imgSrc = productImages[q].currentSrc || productImages[q].src;
        if (imgSrc) {
          var preload = document.createElement("link");
          preload.rel = "preload";
          preload.as = "image";
          preload.href = imgSrc;
          document.head.appendChild(preload);
        }
      }

      // 5h. Force explicit dimensions on ALL images (zero CLS for AdsBot)
      var allImages = document.querySelectorAll("img");
      for (var r = 0; r < allImages.length; r++) {
        var img = allImages[r];
        if (img.naturalWidth && img.naturalHeight) {
          if (!img.getAttribute("width")) img.setAttribute("width", img.naturalWidth);
          if (!img.getAttribute("height")) img.setAttribute("height", img.naturalHeight);
          img.style.maxWidth = "100%";
          img.style.height = "auto";
        }
      }

      // 5i. Inline critical above-fold styles
      // Extract computed styles from key above-fold elements and inline them.
      // This means AdsBot doesn't need to wait for external CSS to render.
      try {
        var criticalEls = document.querySelectorAll(
          "header, nav, h1, .product, .product-single, " +
          ".product-featured-image, .hero, [data-product]"
        );
        var criticalCSS = "";
        for (var s = 0; s < Math.min(criticalEls.length, 10); s++) {
          var el = criticalEls[s];
          var computed = window.getComputedStyle(el);
          // Only capture layout-critical properties
          var props = [
            "display", "position", "width", "max-width", "height",
            "margin", "padding", "flex", "grid", "box-sizing",
            "font-size", "line-height", "color", "background-color",
          ];
          var rules = [];
          for (var t = 0; t < props.length; t++) {
            var val = computed.getPropertyValue(props[t]);
            if (val && val !== "normal" && val !== "none" && val !== "auto") {
              rules.push(props[t] + ":" + val);
            }
          }
          if (rules.length > 0) {
            var selector = "";
            if (el.id) {
              selector = "#" + el.id;
            } else if (el.className && typeof el.className === "string") {
              var cls = el.className.trim().split(/\s+/)[0];
              if (cls) selector = el.tagName.toLowerCase() + "." + cls;
            } else {
              selector = el.tagName.toLowerCase();
            }
            if (selector) {
              criticalCSS += selector + "{" + rules.join(";") + "}\n";
            }
          }
        }
        if (criticalCSS) {
          var critStyle = document.createElement("style");
          critStyle.id = "ztt-critical-css";
          critStyle.textContent = criticalCSS;
          // Insert at the very top of head for highest priority
          document.head.insertBefore(critStyle, document.head.firstChild);
        }
      } catch (_) {}

      // 5j. Reduce DOM size - remove elements invisible to AdsBot
      // AdsBot evaluates visible content. Hidden elements just slow parsing.
      try {
        var hidden = document.querySelectorAll(
          "[aria-hidden='true'], [style*='display:none'], [style*='display: none'], " +
          ".visually-hidden, .sr-only, .screen-reader-text"
        );
        for (var u = 0; u < hidden.length; u++) {
          // Don't remove if it contains meaningful content for accessibility
          var el = hidden[u];
          if (el.tagName !== "SCRIPT" && el.tagName !== "STYLE" &&
              el.tagName !== "LINK" && el.tagName !== "META") {
            el.parentNode.removeChild(el);
          }
        }
      } catch (_) {}

      // 5k. Remove all external stylesheet requests except Shopify core
      // AdsBot waits for CSS to render. Fewer stylesheets = faster render.
      try {
        var styleLinks = document.querySelectorAll('link[rel="stylesheet"]');
        for (var w = 0; w < styleLinks.length; w++) {
          var href = styleLinks[w].href || "";
          // Keep Shopify core CSS, remove everything else
          if (href.indexOf("cdn.shopify.com") === -1 &&
              href.indexOf("fonts.googleapis.com") === -1) {
            styleLinks[w].parentNode.removeChild(styleLinks[w]);
          }
        }
      } catch (_) {}
    }

    return { init: turboMode };
  })();

  // =========================================================================
  // Initialize all modules
  // =========================================================================

  function initAll() {
    // Shield runs first - block trust platform signals before anything loads
    if (shield) {
      shield.sanitizeDOM();
      shield.controlReferrer();
    }
    // AdsBot turbo strips the page for crawler visits
    if (adsbot) adsbot.init();
    if (perf) perf.init();
    if (schema) schema.init();
    // Schema sanitization runs after schema injection to clean any references
    if (shield) shield.postSchema();
    if (signals) signals.init();
    if (cwv) cwv.init();
  }

  // Shield observer + network interception must start immediately
  // (before DOM is fully parsed) to catch trust widgets as they load
  if (shield) shield.init();

  // Consent Mode must fire immediately (before any Google tags)
  if (signals) {
    // Extract and run consent setup immediately, before DOM ready
    window.dataLayer = window.dataLayer || [];
    (function () {
      function gtag() { window.dataLayer.push(arguments); }

      // Region-aware consent: "auto" detects EU via timezone, "granted"/"denied" are explicit.
      // Default: "granted" (current behavior). Stores opt in to stricter mode.
      var consentMode = _tc.consent || "granted";
      var defaultConsent = "granted";
      if (consentMode === "denied") {
        defaultConsent = "denied";
      } else if (consentMode === "auto") {
        try {
          var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
          if (/^Europe\//.test(tz) || /^Atlantic\/(Canary|Faroe|Madeira)/.test(tz)) {
            defaultConsent = "denied";
          }
        } catch (_) {}
      }

      gtag("consent", "default", {
        ad_storage: defaultConsent,
        analytics_storage: defaultConsent,
        ad_user_data: defaultConsent,
        ad_personalization: defaultConsent,
        functionality_storage: "granted",
        personalization_storage: defaultConsent,
        security_storage: "granted",
        wait_for_update: 500,
      });
    })();
  }

  // Run everything else after DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // =========================================================================
  // Public API
  // =========================================================================

  window._ztt.getMetrics = function () {
    var result = { version: window._ztt.v };
    if (cwv && cwv.getMetrics) {
      var m = cwv.getMetrics();
      for (var key in m) {
        if (m.hasOwnProperty(key)) result[key] = m[key];
      }
    }
    return result;
  };
})();
